/*  Objects  */
import { CellContext, Row } from '@tanstack/react-table';
import { Application, ApplicationBusinessOwner } from 'objects/Application';

/*  React  */
import React from 'react';

import { ColumnFilter } from '../Table/types';

/**
 * Reusable filter column configuration for business owners
 */
export const applicationBusinessOwnerFilter: ColumnFilter<unknown> = {
    control: 'Checkbox',
    defaultOptions: [
        {
            options: [
                'managedByPartner',
                'managed3rdParty',
                'noBusinessOwnerRequired',
                'notAssigned'
            ]
        }
    ]
};

/**
 * Filter function for application business owner cells in a table
 * @param {Row<TData>} row - The row data containing the information
 * @param {string} columnId - The ID of the column being filtered
 * @param {string} filterValue - The filter value to match against
 * @returns {boolean} True if the row should be included in filtered results, false otherwise
 */
export function applicationBusinessOwnerFilterFn<TData>(
    row: Row<TData>,
    columnId: string,
    filterValue: string[]
): boolean {
    if (!filterValue || !filterValue?.length) return true;

    if (
        // @ts-expect-error TData
        row.original.noBusinessOwnerRequired === true &&
        filterValue.includes('noBusinessOwnerRequired')
    )
        return true;

    if (
        // @ts-expect-error TData
        row.original.managedByPartner === true &&
        filterValue.includes('managedByPartner')
    )
        return true;

    if (
        // @ts-expect-error TData
        row.original.managed3rdParty === true &&
        filterValue.includes('managed3rdParty')
    )
        return true;

    if (
        // @ts-expect-error TData
        !row.original.noBusinessOwnerRequired &&
        // @ts-expect-error TData
        !row.original.managedByPartner &&
        // @ts-expect-error TData
        !row.original.managed3rdParty &&
        // @ts-expect-error TData
        !row.original.businessOwnerPlatformUserId &&
        filterValue.includes('notAssigned')
    )
        return true;

    return false;
}

/**
 * Component that renders a cell containing application business owner information
 * Uses the Application and ApplicationBusinessOwner components to display the data
 * @param props - Cell context props from react-table containing cell and row data
 * @returns JSX element displaying the business owner information
 */
export const ApplicationBusinessOwnerCell = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    props: CellContext<any, unknown>
) => {
    const { cell, row } = props;

    // If the column does not have an accesorKey then the value will come from row.original.
    // Otherwise the business owner properties will come from the object given as the accesorKey.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const accessorValue = cell.getValue() as any;

    return (
        <Application
            data={row.original.application}
            rerender={row.original.rerendered}
        >
            <ApplicationBusinessOwner
                // @ts-expect-error ApplicationBusinessOwner is not fully typed.
                businessOwnerPlatformUserId={
                    accessorValue
                        ? accessorValue.businessOwnerPlatformUserId
                        : row.original.businessOwnerPlatformUserId
                }
                noBusinessOwnerRequired={
                    accessorValue
                        ? accessorValue.noBusinessOwnerRequired
                        : row.original.noBusinessOwnerRequired
                }
                managedByPartner={
                    accessorValue
                        ? accessorValue.managedByPartner
                        : row.original.managedByPartner
                }
                managed3rdParty={
                    accessorValue
                        ? accessorValue.managed3rdParty
                        : row.original.managed3rdParty
                }
            />
        </Application>
    );
};
